// Section observability — fires three kinds of events to /api/track-event:
//
//   1. section-view   on first time the section enters the viewport
//                     (≥25 % visible) per session
//   2. section-dwell  every time the section LEAVES the viewport (or
//                     the tab is hidden / unloaded) with the elapsed
//                     visible-ms attached
//   3. (Vercel mirror) the same first-view event also fires through
//                     @vercel/analytics' track('section-view', …) so
//                     Vercel's dashboard stays useful as a fallback
//
// Editor traffic to /admin/* and /analysis/* is excluded.

import { useEffect, useRef } from 'react';
import { track } from '@vercel/analytics';
import { trackSectionDwell, trackSectionView } from './trackEvent';

const seenThisSession = new Set<string>();

function isAdminPath(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p.startsWith('/admin') || p.startsWith('/analysis');
}

export function useSectionView<T extends Element>(
  sectionKey: string,
): React.RefCallback<T> {
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Per-section state so cleanup can reach into it.
  const stateRef = useRef<{
    visibleStart: number | null;
    flush: () => void;
    onVisibility: () => void;
    onPageHide: () => void;
  } | null>(null);

  // Cleanup on component unmount.
  useEffect(() => {
    return () => {
      const s = stateRef.current;
      if (s) {
        s.flush();
        document.removeEventListener('visibilitychange', s.onVisibility);
        window.removeEventListener('pagehide', s.onPageHide);
      }
      observerRef.current?.disconnect();
      observerRef.current = null;
      stateRef.current = null;
    };
  }, []);

  return (el: T | null) => {
    // Tear down previous observer when the ref re-attaches.
    observerRef.current?.disconnect();
    if (stateRef.current) {
      stateRef.current.flush();
      document.removeEventListener('visibilitychange', stateRef.current.onVisibility);
      window.removeEventListener('pagehide', stateRef.current.onPageHide);
      stateRef.current = null;
    }

    if (!el) return;
    if (isAdminPath()) return;

    // FeatureSection wraps its "live" children in a `display: contents`
    // div so the wrapper doesn't participate in layout. That wrapper
    // has a zero-area bounding rect, which means IntersectionObserver
    // can never see it as intersecting (and section-view / section-dwell
    // would silently never fire). Detect that case and observe the
    // first real DOM child instead — every section component renders a
    // single root element with a real box, so this is layout-safe.
    let observeTarget: Element = el;
    if (el instanceof HTMLElement && el.style.display === 'contents') {
      const firstChild = el.firstElementChild;
      if (firstChild) observeTarget = firstChild;
    }

    const state = {
      visibleStart: null as number | null,
      flush: () => {
        if (state.visibleStart === null) return;
        const duration = Date.now() - state.visibleStart;
        state.visibleStart = null;
        trackSectionDwell(sectionKey, duration);
      },
      onVisibility: () => {
        if (document.visibilityState === 'hidden') state.flush();
      },
      onPageHide: () => {
        state.flush();
      },
    };
    stateRef.current = state;

    document.addEventListener('visibilitychange', state.onVisibility);
    window.addEventListener('pagehide', state.onPageHide);

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            // First-view event (deduped per session for both Vercel +
            // self-hosted).
            if (!seenThisSession.has(sectionKey)) {
              seenThisSession.add(sectionKey);
              try {
                track('section-view', { section: sectionKey });
              } catch {
                /* @vercel/analytics not loaded — fail silent */
              }
              trackSectionView(sectionKey);
            }
            // Begin dwell timer. Already-running timers (e.g. extra
            // fires of the same threshold during scroll) are a no-op.
            if (state.visibleStart === null) {
              state.visibleStart = Date.now();
            }
          } else {
            // Section left the viewport — flush the dwell.
            state.flush();
          }
        }
      },
      { threshold: 0.25 },
    );
    obs.observe(observeTarget);
    observerRef.current = obs;
  };
}
